import { lazy, type ReactElement } from 'react'
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'
import { useAuth, isPublicPath } from '@/utils/auth'
import { usePermission } from '@/hooks/usePermission'
import { SpinLoading } from 'antd-mobile'

// ===== 懒加载布局组件 =====
const MainLayout = lazy(() => import('@/layouts/MainLayout'))

// ===== 懒加载页面组件 =====
const Workbench = lazy(() => import('@/pages/workbench'))
const Mine = lazy(() => import('@/pages/user'))

// 认证相关
const Login = lazy(() => import('@/pages/auth/Login'))
const Profile = lazy(() => import('@/pages/auth/Profile'))
const WxCallback = lazy(() => import('@/pages/auth/WxCallback'))

// 用户相关
const ChangePassword = lazy(() => import('@/pages/user/ChangePassword'))
const Statistics = lazy(() => import('@/pages/user/Statistics'))
const StatisticsDetail = lazy(() => import('@/pages/user/StatisticsDetail'))
const SellRecord = lazy(() => import('@/pages/user/SellRecord'))

// 物料信息
const MaterialInfo = lazy(() => import('@/pages/material-info'))

// 分销定价
const DistributionPricing = lazy(() => import('@/pages/distribution-pricing'))
const DistributionPricingDetail = lazy(() => import('@/pages/distribution-pricing/Detail'))

// 提货单据
const PickupOrder = lazy(() => import('@/pages/pickup-order'))
const PickupOrderDetailSummary = lazy(() => import('@/pages/pickup-order/DetailSummary'))
const PickupOrderAdd = lazy(() => import('@/pages/pickup-order/Add'))
const PickupOrderShare = lazy(() => import('@/pages/pickup-order/Share'))

// 休假报备
const VacationReport = lazy(() => import('@/pages/vacation-report'))
const VacationReportAdd = lazy(() => import('@/pages/vacation-report/Add'))

// 报销登记
const Reimbursement = lazy(() => import('@/pages/reimbursement'))
const ReimbursementList = lazy(() => import('@/pages/reimbursement/List'))
const ReimbursementAdd = lazy(() => import('@/pages/reimbursement/Add'))

// 客户管理
const Customer = lazy(() => import('@/pages/customer'))
const CustomerDetail = lazy(() => import('@/pages/customer/Detail'))

// 登记管理
const Registration = lazy(() => import('@/pages/registration/index'))
const RegistrationAdd = lazy(() => import('@/pages/registration/Add/index'))
const RegistrationDetail = lazy(() => import('@/pages/registration/Detail'))
const RegistrationUserForm = lazy(() => import('@/pages/registration/UserForm'))

// 摩托登记
const MotoRegistration = lazy(() => import('@/pages/motoRegistration/index'))
const MotoRegistrationAdd = lazy(() => import('@/pages/motoRegistration/Add/index'))
const MotoRegistrationDetail = lazy(() => import('@/pages/motoRegistration/Detail'))

// 配件管理
const Parts = lazy(() => import('@/pages/parts'))
const PartDetail = lazy(() => import('@/pages/parts/Detail'))
const PartEdit = lazy(() => import('@/pages/parts/Edit'))

// 车辆管理
const VehicleManage = lazy(() => import('@/pages/vehicle/index'))
const VehicleDetail = lazy(() => import('@/pages/vehicle/Detail'))
const VehicleEdit = lazy(() => import('@/pages/vehicle/Edit'))

// 其他
const Niuyoubao = lazy(() => import('@/pages/niuyoubao'))

// 待办
const Todo = lazy(() => import('@/pages/todo'))
const TodoAdd = lazy(() => import('@/pages/todo/Add'))
const TodoDetail = lazy(() => import('@/pages/todo/Detail'))
const TodoUserForm = lazy(() => import('@/pages/todo/UserForm'))
const TodoUserDetail = lazy(() => import('@/pages/todo/UserDetail'))

// 售前待办
const PreSaleTodo = lazy(() => import('@/pages/preSaleTodo'))
const PreSaleTodoAdd = lazy(() => import('@/pages/preSaleTodo/Add'))
const PreSaleTodoDetail = lazy(() => import('@/pages/preSaleTodo/Detail'))

// 通用
const NotFound = lazy(() => import('@/pages/common/NotFound'))

// ===== 路由守卫组件 =====

// 全局加载状态
const RouteLoading = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <SpinLoading color="primary" />
  </div>
)

/**
 * 路由守卫组件
 * 检查用户登录状态，未登录则跳转到首页
 */
function RouteGuard({ children }: { children: ReactElement }) {
  const { isAuthenticated, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return <RouteLoading />
  }

  const isPublic = isPublicPath(location.pathname)

  if (!isAuthenticated && !isPublic) {
    return <Navigate to="/workbench" replace state={{ from: location }} />
  }

  return children
}

/**
 * 管理员路由守卫组件
 */
function AdminGuard({ children }: { children: ReactElement }) {
  const { isAdmin } = usePermission()
  const { loading } = useAuth()

  if (loading) {
    return <RouteLoading />
  }

  if (!isAdmin) {
    return <Navigate to="/workbench" replace />
  }

  return children
}

/**
 * 路由守卫包装器 - 简化重复的路由守卫包裹
 */
function withRouteGuard(
  Component: ReactElement,
  options?: { admin?: boolean }
) {
  let element = <RouteGuard>{Component}</RouteGuard>

  if (options?.admin) {
    element = (
      <RouteGuard>
        <AdminGuard>{Component}</AdminGuard>
      </RouteGuard>
    )
  }

  return element
}

// ===== 路由配置 =====
export const router = createBrowserRouter([
  // 根路由重定向
  {
    path: '/',
    element: <Navigate to="/workbench" replace />,
  },

  // 布局路由（工作台、我的）
  {
    path: '/',
    element: <MainLayout />,
    children: [
      { path: 'workbench', element: <Workbench /> },
      { path: 'mine', element: <Mine /> },
    ],
  },

  // 公开路由（无需登录）
  { path: '/login', element: <Login /> },
  { path: '/wx-callback', element: <WxCallback /> },
  { path: '/user-form/:inviteCode', element: <RegistrationUserForm /> },
  { path: '/todo-user-form/:uniqueId', element: <TodoUserForm /> },
  { path: '/todo-user-detail/:uniqueId', element: <TodoUserDetail /> },
  { path: '/pickup-order-share/:id', element: <PickupOrderShare /> },

  // 客户管理（需登录）
  {
    path: '/customer',
    element: withRouteGuard(<Customer />),
  },
  {
    path: '/customer/:id',
    element: withRouteGuard(<CustomerDetail />),
  },

  // 登记管理（需登录）
  {
    path: '/registration',
    element: withRouteGuard(<Registration />),
  },
  {
    path: '/registration/add',
    element: withRouteGuard(<RegistrationAdd />),
  },
  {
    path: '/registration/:id',
    element: withRouteGuard(<RegistrationDetail />),
  },
  {
    path: '/registration/:id/edit',
    element: withRouteGuard(<RegistrationAdd />),
  },

  // 摩托登记（需登录）
  {
    path: '/moto-registration',
    element: withRouteGuard(<MotoRegistration />),
  },
  {
    path: '/moto-registration/add',
    element: withRouteGuard(<MotoRegistrationAdd />),
  },
  {
    path: '/moto-registration/:id',
    element: withRouteGuard(<MotoRegistrationDetail />),
  },
  {
    path: '/moto-registration/:id/edit',
    element: withRouteGuard(<MotoRegistrationAdd />),
  },

  // 配件管理（需登录）
  {
    path: '/parts',
    element: withRouteGuard(<Parts />),
  },
  {
    path: '/parts/:id',
    element: withRouteGuard(<PartDetail />),
  },
  {
    path: '/parts/add',
    element: withRouteGuard(<PartEdit />),
  },
  {
    path: '/parts/:id/edit',
    element: withRouteGuard(<PartEdit />),
  },

  // 车辆管理（需登录）
  {
    path: '/vehicle',
    element: withRouteGuard(<VehicleManage />),
  },
  {
    path: '/vehicle/add',
    element: withRouteGuard(<VehicleEdit />),
  },
  {
    path: '/vehicle/:id',
    element: withRouteGuard(<VehicleDetail />),
  },
  {
    path: '/vehicle/:id/edit',
    element: withRouteGuard(<VehicleEdit />),
  },

  // 用户相关（需登录）
  {
    path: '/profile',
    element: withRouteGuard(<Profile />),
  },
  {
    path: '/statistics',
    element: withRouteGuard(<Statistics />),
  },
  {
    path: '/statistics/detail',
    element: withRouteGuard(<StatisticsDetail />),
  },
  {
    path: '/sell-record',
    element: withRouteGuard(<SellRecord />, { admin: true }),
  },
  {
    path: '/change-password',
    element: withRouteGuard(<ChangePassword />),
  },

  // 其他（需登录）
  {
    path: '/niuyoubao',
    element: withRouteGuard(<Niuyoubao />),
  },

  // 物料信息（需登录）
  {
    path: '/material-info',
    element: withRouteGuard(<MaterialInfo />),
  },

  // 分销定价（需登录）
  {
    path: '/distribution-pricing',
    element: withRouteGuard(<DistributionPricing />),
  },
  {
    path: '/distribution-pricing/detail',
    element: withRouteGuard(<DistributionPricingDetail />),
  },

  // 提货单据（需登录）
  {
    path: '/pickup-order',
    element: withRouteGuard(<PickupOrder />),
  },
  {
    path: '/pickup-order/add',
    element: withRouteGuard(<PickupOrderAdd />),
  },
  {
    path: '/pickup-order/:id',
    element: withRouteGuard(<PickupOrderDetailSummary />),
  },
  {
    path: '/pickup-order/:id/edit',
    element: withRouteGuard(<PickupOrderAdd />),
  },

  // 待办（需登录）
  {
    path: '/todo',
    element: withRouteGuard(<Todo />),
  },
  {
    path: '/todo/add',
    element: withRouteGuard(<TodoAdd />),
  },
  {
    path: '/todo/:id',
    element: withRouteGuard(<TodoDetail />),
  },
  {
    path: '/todo/:id/edit',
    element: withRouteGuard(<TodoAdd />),
  },

  // 售前待办（需登录）
  {
    path: '/pre-sale-todo',
    element: withRouteGuard(<PreSaleTodo />),
  },
  {
    path: '/pre-sale-todo/add',
    element: withRouteGuard(<PreSaleTodoAdd />),
  },
  {
    path: '/pre-sale-todo/:id',
    element: withRouteGuard(<PreSaleTodoDetail />),
  },
  {
    path: '/pre-sale-todo/:id/edit',
    element: withRouteGuard(<PreSaleTodoAdd />),
  },

  // 休假报备（需登录）
  {
    path: '/vacation-report',
    element: withRouteGuard(<VacationReport />),
  },
  {
    path: '/vacation-report/add',
    element: withRouteGuard(<VacationReportAdd />),
  },
  {
    path: '/vacation-report/:id/edit',
    element: withRouteGuard(<VacationReportAdd />),
  },

  // 报销登记（需登录）
  {
    path: '/reimbursement',
    element: withRouteGuard(<Reimbursement />),
  },
  {
    path: '/reimbursement/:employeeId',
    element: withRouteGuard(<ReimbursementList />),
  },
  {
    path: '/reimbursement/:employeeId/add',
    element: withRouteGuard(<ReimbursementAdd />),
  },
  {
    path: '/reimbursement/:employeeId/:id/edit',
    element: withRouteGuard(<ReimbursementAdd />),
  },

  // 404
  {
    path: '*',
    element: <NotFound />,
  },
])

export default router
